import * as Sentry from '@sentry/react';
import { Environments } from '../constants/environments';
import { IUser } from '../interfaces/IUser';
import { customHistory } from './history.utils';
import { Route } from 'react-router-dom';
import posthog from 'posthog-js';

export const initMonitoringService = (
	dsn: string,
	environment: string,
	release: string,
	sentryOrg: string,
	sentryProjectId: number
): void => {
	// Same set `assertMonitoringConfig` below already validates config for. Reporting only
	// from prod meant every regression was found by a customer rather than in staging or QA,
	// where it is cheap to fix — and it made the two lists silently disagree about which
	// environments are meant to be monitored at all.
	const allowedEnvironments: Environments[] = [
		Environments.prod,
		Environments.prodJp,
		Environments.prodSg,
		Environments.staging,
		Environments.qa,
	];
	if (!allowedEnvironments.includes(environment as Environments)) {
		return;
	}

	Sentry.init({
		dsn: dsn,
		environment,
		release,
		integrations: [
			Sentry.replayIntegration({
				// Worker names, live locations and safety-incident detail are on screen in this
				// product. A replay is a debugging aid; none of that has to reach a vendor for it
				// to do its job.
				maskAllText: true,
				blockAllMedia: true,
			}),
			// `enableInp` is gone since v8 — INP is measured by default and the option no longer
			// exists.
			Sentry.reactRouterV5BrowserTracingIntegration({ history: customHistory }),
			// Forwards Sentry errors into PostHog so an exception and the session that produced it
			// sit side by side. Functional form, replacing the `new posthog.SentryIntegration(...)`
			// class that matched Sentry v7's class-based integrations.
			posthog.sentryIntegration({ organization: sentryOrg, projectId: sentryProjectId }),
		],
		tracesSampleRate: 0.1,
		/**
		 * Sentry's replay is the one attached to an ERROR — PostHog owns behavioural replay and
		 * its allowance is sized for that. The blanket 10% session sampler that used to sit here
		 * spent the (50/month) budget on ordinary sessions, so by the time something actually
		 * broke there was no quota left to record it with. Zero here, everything on error.
		 */
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 1.0,
	});
};

export const MonitoringRoute = Sentry.withSentryRouting(Route);

/**
 * Reports monitoring/analytics keys that are empty in an environment that is supposed to be
 * reporting, instead of letting an SDK initialise with `undefined` and silently drop everything.
 *
 * This exists because of a specific six-year outage: `amplitudeApiKey` never reached the browser
 * config, `BIService` called `init(undefined)`, and every product event was discarded by a
 * perfectly healthy-looking app. A volume alert cannot catch that — there is no drop to detect
 * when the value was always zero — so the check has to happen where the key is read.
 *
 * `console.error` is deliberately not a fallback but a peer: if the missing key IS the Sentry DSN,
 * `captureMessage` is itself a no-op, and the console is the only channel left.
 */
export const assertMonitoringConfig = (
	keys: Record<string, string | number | undefined>,
	environment: string
): void => {
	const reportingEnvironments: string[] = [
		Environments.prod,
		Environments.prodJp,
		Environments.prodSg,
		Environments.staging,
		Environments.qa,
	];
	if (!reportingEnvironments.includes(environment)) {
		return;
	}

	const missingKeys: string[] = Object.entries(keys)
		.filter(([, value]) => value === undefined || value === null || value === '')
		.map(([name]) => name);

	if (missingKeys.length === 0) {
		return;
	}

	const message = `Monitoring config incomplete in ${environment}: ${missingKeys.join(', ')}`;
	console.error(message);
	captureMessage('Monitoring config incomplete', { missingKeys, environment });
};

export const initSentryUser = (user: IUser): void => {
	Sentry.setUser({
		id: user.username,
		username: user.username,
		userId: user._id,
		email: user.email,
		roleType: user.permissions.roleType,
	});
};

export const setSentryProjectId = (projectId: string): void => {
	Sentry.setTag('projectId', projectId);
};

const setMetadata = (scope: any, metadata?: any) => {
	if (!metadata) {
		return;
	}

	Object.keys(metadata).forEach((key) => {
		scope.setExtra(key, metadata[key]);
	});
};

export const captureError = (err, metadata?: any) => {
	Sentry.withScope((scope) => {
		setMetadata(scope, metadata);
		Sentry.captureException(err);
	});
};

export const captureMessage = (message: string, metadata?: any) => {
	Sentry.withScope((scope) => {
		setMetadata(scope, metadata);
		Sentry.captureMessage(message);
	});
};

export const startSpan = (name: string, callback: () => Promise<void>) => {
	return Sentry.startSpan({ name }, async () => {
		await callback();
	});
};
